import { apiBaseUrl, gatewayHeaders } from "@/config/api";

// How long a logout waits for the server before leaving anyway. A dead network
// must not trap someone on a page they asked to leave.
const SERVER_LOGOUT_TIMEOUT_MS = 2000;

/**
 * The auth cookies (access_token_<company>, refresh_token_<company>) are
 * httpOnly: the browser will not let script read or delete them, so only a
 * response from the server can. The gateway's existing logout endpoint does
 * exactly that.
 *
 * A plain fetch rather than baseApi on purpose — a 401 there triggers a token
 * refresh, which would end up back here. gatewayHeaders() carries X-Subdomain,
 * which tells the gateway which company's cookie names to clear.
 */
const clearAuthCookiesOnServer = async () => {
  const request = fetch(`${apiBaseUrl}/authentication/logout`, {
    method: "POST",
    credentials: "include",
    // X-Session-Kind makes the gateway end the agent session, not a widget
    // session that happens to share the browser.
    headers: gatewayHeaders(),
    // Lets the request finish even if the timeout wins and we navigate away.
    keepalive: true,
  }).catch(() => undefined);

  const timeout = new Promise((resolve) => setTimeout(resolve, SERVER_LOGOUT_TIMEOUT_MS));
  await Promise.race([request, timeout]);
};

/**
 * Expires every cookie script can see. A cookie only goes away when the delete
 * names the same path and domain it was set with, and those are not readable
 * back, so each name is expired against the host and every parent domain.
 */
const clearReadableCookies = () => {
  const hostParts = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < hostParts.length - 1; i++) {
    domains.push(`; domain=.${hostParts.slice(i).join(".")}`);
  }

  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0].trim();
    if (!name) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
};

/** Wipes everything this origin keeps in the browser for the signed-in user. */
export const clearClientSession = () => {
  localStorage.clear();
  sessionStorage.clear();
  clearReadableCookies();
};

/**
 * The single way a session ends — whether the user logged out or the session
 * expired. Removes the httpOnly auth cookies, then all browser storage and
 * readable cookies, then hard-navigates to /login so no in-memory state (Redux
 * cache, sockets) survives either.
 */
export const endSession = async () => {
  await clearAuthCookiesOnServer();
  clearClientSession();

  // Already on /login: a redirect would only reload the page, and a request
  // that 401s on load would turn that into a loop.
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};
